/**
 * CreateInboxDialog - Modal único para criação de inboxes
 *
 * Substitui o antigo wizard de 4 etapas por uma única tela:
 *   1. Grid de canais (clique único)
 *   2. Formulário em 2 colunas: nome/identificador/provedor + credenciais dinâmicas
 *   3. Botão único "Criar inbox"
 *
 * REGRAS:
 * - Etapa de VERSÃO removida da UI: usa sempre a versão padrão (is_default) do
 *   provedor, ou a primeira disponível.
 * - Provedor com apenas 1 opção é auto-selecionado e exibido como campo read-only.
 * - Fluxos especiais preservados: Meta Embedded Signup, Instagram Setup Guide,
 *   vínculo de instância, conflito de instance_id e limites de plano.
 *
 * RELEASE:
 *   2026-08-08 v4.0.0 - Leiaute moderno (gradientes/blooms) e renomeação para "inbox".
 *   2026-08-08 v3.0.0 - Fim do wizard: modal unificado em 2 colunas.
 *   2026-01-16 v2.0.0 - Suporte a Meta Embedded Signup.
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PhoneInput } from '@/components/ui/phone-input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  Clock,
  Info,
  Zap,
  ShoppingCart,
  Inbox as InboxIcon,
  SlidersHorizontal,
  
  ArrowRight,
} from 'lucide-react';
import inboxIllustration from '@/assets/inbox-channels-burst.png';
import { AgentSignaturePreviewCard } from '@/components/tenant/inbox/AgentSignaturePreviewCard';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { useResourceLimits } from '@/hooks/useResourceLimits';
import { LimitReachedModal } from '@/components/tenant/LimitReachedModal';
import { useAvailableInstances } from '@/hooks/useProviderInstances';
import {
  useProviderInstanceAutoProvision,
  type ProvisionedInstance,
} from '@/hooks/useProviderInstanceAutoProvision';
import { useTenantPlansViewMode } from '@/hooks/tenant/useTenantPlansV2';
import { MetaEmbeddedSignupDialog } from '@/components/tenant/inbox/MetaEmbeddedSignupDialog';
import { InstagramSetupGuide } from '@/components/tenant/inbox/InstagramSetupGuide';
import { getInstagramWebhookUrl } from '@/lib/supabaseHelpers';
import { useNavigate } from 'react-router-dom';
import { ChannelPicker } from '@/components/tenant/inbox/ChannelPicker';
import { InboxInstanceNotice } from '@/components/tenant/inbox/InboxInstanceNotice';

import {
  InboxCredentialsFields,
  type CredentialField,
} from '@/components/tenant/inbox/InboxCredentialsFields';

// ============= TIPOS =============
type ChannelType = 'whatsapp' | 'whatsapp_oficial' | 'instagram' | 'messenger' | 'telegram' | 'webchat';
type VersionStatus = 'development' | 'beta' | 'stable' | 'deprecated';

// Interface para provider - exibe display_name ao invés de name real
interface Provider {
  id: string;
  name: string;
  display_name?: string;
  slug: string;
  channel_type: ChannelType;
  logo_url?: string;
  config_schema?: Record<string, unknown>;
  /** Versões retornadas pela RPC segura de catálogo; evita SELECT direto em provider_versions. */
  versions?: ProviderVersion[];
}

// Interface para campo do schema de credenciais
interface CredentialSchemaField {
  type: string;
  label: string;
  required?: boolean;
  placeholder?: string;
  description?: string;
}

// Interface para configuração de versão do provider
interface ProviderVersionConfig {
  credentials_schema?: Record<string, CredentialSchemaField>;
  [key: string]: unknown;
}

// Interface para versão do provider
interface ProviderVersion {
  id: string;
  provider_id: string;
  version: string;
  status: VersionStatus;
  is_default: boolean;
  config?: ProviderVersionConfig;
}

// Linha retornada pela RPC tenant_list_inbox_providers
interface TenantInboxProviderRpcRow {
  id: string;
  name: string;
  display_name: string | null;
  slug: string;
  channel_type: ChannelType;
  logo_url: string | null;
  config_schema: Record<string, unknown> | null;
  versions: unknown;
}

interface CreateInboxDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  onSuccess: () => void;
}

/**
 * Resolve a versão a ser utilizada para o provider (sem expor a escolha ao usuário).
 * Prioriza a versão padrão; caso não exista, usa a primeira da lista.
 */
function resolveVersion(provider?: Provider): ProviderVersion | null {
  const list = provider?.versions || [];
  if (list.length === 0) return null;
  return list.find((v) => v.is_default) || list[0];
}

export function CreateInboxDialog({ open, onOpenChange, tenantId, onSuccess }: CreateInboxDialogProps) {
  const navigate = useNavigate();

  // ============= ESTADOS =============
  const [selectedChannel, setSelectedChannel] = useState<ChannelType | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [instanceIdConflict, setInstanceIdConflict] = useState<string | null>(null);
  const [checkingInstanceId, setCheckingInstanceId] = useState(false);
  const [limitReachedModalOpen, setLimitReachedModalOpen] = useState(false);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);

  // Estados para Meta Embedded Signup
  const [metaSignupDialogOpen, setMetaSignupDialogOpen] = useState(false);
  const [pendingMetaInbox, setPendingMetaInbox] = useState<{ id: string; name: string } | null>(null);

  // Hook de limites de recursos
  const { profile } = useAuth();
  const { limits, isLoadingLimits, refetchLimits } = useResourceLimits();

  // Provider selecionado e versão resolvida automaticamente
  const selectedProviderData = useMemo(
    () => providers.find((p) => p.id === selectedProvider),
    [providers, selectedProvider],
  );
  const resolvedVersion = useMemo(() => resolveVersion(selectedProviderData), [selectedProviderData]);

  // REGRA: Somente webchat NÃO requer instância pré-provisionada
  const providerSlug = selectedProviderData?.slug;
  const PROVIDERS_WITHOUT_INSTANCE_MANAGEMENT = ['webchat'];
  const requiresInstance = providerSlug
    ? !PROVIDERS_WITHOUT_INSTANCE_MANAGEMENT.includes(providerSlug)
    : false;

  // Verifica se é provider meta-instagram (fluxo de credenciais manuais)
  const isMetaInstagram = providerSlug === 'meta-instagram';

  const {
    availableInstances,
    hasPendingInstances,
    isLoading: loadingInstances,
    useInstance,
  } = useAvailableInstances(tenantId, requiresInstance ? providerSlug : undefined);

  // ---------------------------------------------------------------------------
  // Criação automática de instância (quando há cota livre)
  // Reaproveita as mesmas RPCs da aba "Instâncias" — nenhuma regra nova.
  // ---------------------------------------------------------------------------
  const { data: plansViewMode } = useTenantPlansViewMode();
  const isV1Plan = plansViewMode === 'integracao_v1';

  const needsInstanceProvision =
    requiresInstance && !loadingInstances && availableInstances.length === 0 && !hasPendingInstances;

  const {
    hasQuota,
    isLoadingQuota,
    autoProvision,
  } = useProviderInstanceAutoProvision({
    tenantId,
    providerId: selectedProvider,
    enabled: isV1Plan && needsInstanceProvision,
  });

  // Pode provisionar automaticamente: plano V1 + sem instância + cota livre
  const canAutoProvision = isV1Plan && needsInstanceProvision && hasQuota;

  /**
   * Indica que o provider selecionado exige instância e não há nenhuma disponível
   * (nem pendente, nem cota livre) — usado para exibir e focar o aviso comercial.
   */
  const showInstanceRequired =
    requiresInstance &&
    availableInstances.length === 0 &&
    !hasPendingInstances &&
    !loadingInstances &&
    !isLoadingQuota &&
    !canAutoProvision;

  /** Referência para dar foco/scroll no aviso de instância ausente */
  const instanceNoticeRef = useRef<HTMLDivElement>(null);

  // Ao detectar falta de instância, traz o aviso para o campo de visão do usuário
  useEffect(() => {
    if (!showInstanceRequired) return;

    const timer = window.setTimeout(() => {
      instanceNoticeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [showInstanceRequired, selectedProvider]);

  // Nome amigável do provider para mensagens ao tenant
  const getProviderDisplayName = () => selectedProviderData?.display_name || 'Conector WhatsApp';

  // ============= EFFECTS =============

  // Recarrega limites sempre que o modal abre
  useEffect(() => {
    if (open) refetchLimits();
  }, [open, refetchLimits]);

  // Busca providers quando o canal é selecionado
  useEffect(() => {
    if (!selectedChannel) return;

    setSelectedProvider(null);
    setProviders([]);
    setSelectedInstanceId(null);
    setCredentials({});

    if (!limits || isLoadingLimits) {
      setLoadingProviders(true);
      return;
    }

    fetchProviders(selectedChannel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChannel, limits?.plan_id, limits?.available_providers?.join('|'), isLoadingLimits]);

  // Auto-seleção do provedor quando existe apenas um (ou nenhuma escolha feita)
  useEffect(() => {
    if (providers.length === 1 && !selectedProvider) {
      setSelectedProvider(providers[0].id);
    }
  }, [providers, selectedProvider]);

  // Reset do estado quando o dialog fecha
  useEffect(() => {
    if (!open) {
      setSelectedChannel(null);
      setSelectedProvider(null);
      setProviders([]);
      setName('');
      setIdentifier('');
      setCredentials({});
      setSignatureEnabled(false);
      setInstanceIdConflict(null);
      setCheckingInstanceId(false);
      setLimitReachedModalOpen(false);
      setSelectedInstanceId(null);
      setMetaSignupDialogOpen(false);
      setPendingMetaInbox(null);
    }
  }, [open]);

  // Debounce para verificar conflito de instance_id ao digitar
  useEffect(() => {
    const instanceId = credentials['instance_id'];

    if (!instanceId || !tenantId) {
      setInstanceIdConflict(null);
      return;
    }

    const timeoutId = setTimeout(() => {
      checkInstanceIdConflict(instanceId);
    }, 500);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials['instance_id'], tenantId]);

  // ============= FUNÇÕES DE DADOS =============

  /**
   * Verifica se o instance_id já está em uso por outra inbox ativa do mesmo tenant
   */
  const checkInstanceIdConflict = async (instanceId: string) => {
    if (!instanceId || !tenantId) {
      setInstanceIdConflict(null);
      return;
    }

    setCheckingInstanceId(true);
    try {
      const { data: conflictingInboxes, error } = await supabase
        .from('inbox_credentials')
        .select(`
          inbox_id,
          inbox:inboxes!inner(id, name, status, tenant_id)
        `)
        .filter('credentials->>instance_id', 'eq', instanceId)
        .eq('inbox.status', 'active')
        .eq('inbox.tenant_id', tenantId);

      if (error) {
        logger.error('[CreateInboxDialog]', 'Erro ao verificar conflito de instance_id', error);
        setInstanceIdConflict(null);
        return;
      }

      if (conflictingInboxes && conflictingInboxes.length > 0) {
        const conflictingInbox = conflictingInboxes[0] as {
          inbox_id: string;
          inbox: { id: string; name: string; status: string; tenant_id: string } | null;
        };
        const conflictingName = conflictingInbox.inbox?.name || 'Desconhecida';
        logger.warn('[CreateInboxDialog]', 'Conflito de instance_id detectado', {
          instanceId,
          conflictingName,
        });
        setInstanceIdConflict(conflictingName);
      } else {
        setInstanceIdConflict(null);
      }
    } catch (error) {
      logger.error('[CreateInboxDialog]', 'Erro ao verificar instance_id', error);
      setInstanceIdConflict(null);
    } finally {
      setCheckingInstanceId(false);
    }
  };

  /**
   * Busca providers do canal selecionado usando RPC segura.
   * A RPC valida o tenant, resolve o plano efetivo e aplica a Matriz de Planos.
   */
  const fetchProviders = async (channel: ChannelType) => {
    setLoadingProviders(true);
    try {
      const { data, error } = await supabase.rpc('tenant_list_inbox_providers', {
        p_tenant_hint: tenantId || null,
        p_channel_type: channel,
      });

      if (error) throw error;

      const normalizedProviders: Provider[] = ((data || []) as unknown as TenantInboxProviderRpcRow[]).map(
        (provider) => ({
          id: provider.id,
          name: provider.name,
          display_name: provider.display_name || undefined,
          slug: provider.slug,
          channel_type: provider.channel_type,
          logo_url: provider.logo_url || undefined,
          config_schema: provider.config_schema || undefined,
          versions: Array.isArray(provider.versions) ? (provider.versions as ProviderVersion[]) : [],
        }),
      );

      logger.info('[CreateInboxDialog]', 'Providers disponíveis para canal', {
        planId: limits?.plan_id || 'desconhecido',
        channel,
        foundCount: normalizedProviders.length,
      });
      setProviders(normalizedProviders);
    } catch (error) {
      logger.error('[CreateInboxDialog]', 'Erro ao carregar providers', error);
      toast.error('Erro ao carregar provedores');
    } finally {
      setLoadingProviders(false);
    }
  };

  /**
   * Retorna campos de credencial, filtrando client_token (preenchido via secret).
   * Providers Meta não exibem campos: as credenciais vêm da instância selecionada.
   */
  const getCredentialFields = (): CredentialField[] => {
    const provider = selectedProviderData;

    if (provider?.slug === 'meta-whatsapp' || provider?.slug === 'meta-instagram') {
      return [];
    }

    const processArraySchema = (
      fields: {
        name: string;
        label?: string;
        type?: string;
        required?: boolean;
        placeholder?: string;
        description?: string;
        default?: string;
      }[],
    ): CredentialField[] =>
      fields
        .filter((field) => field.name !== 'client_token')
        .map((field) => ({
          key: field.name,
          label: field.label || field.name,
          type: field.type || 'text',
          required: field.required !== false,
          placeholder: field.placeholder || field.label || field.name,
          description: field.description,
          defaultValue: field.default,
        }));

    const processObjectSchema = (schema: Record<string, CredentialSchemaField>): CredentialField[] =>
      Object.entries(schema)
        .filter(([key]) => key !== 'client_token')
        .map(([key, config]) => ({
          key,
          label: config.label || key,
          type: config.type || 'text',
          required: config.required !== false,
          placeholder: config.placeholder || config.label || key,
          description: config.description,
        }));

    // Prioriza o schema da versão resolvida automaticamente
    if (resolvedVersion?.config?.credentials_schema) {
      const schema = resolvedVersion.config.credentials_schema as unknown;
      if (
        typeof schema === 'object' &&
        schema !== null &&
        'fields' in schema &&
        Array.isArray((schema as { fields: unknown[] }).fields)
      ) {
        return processArraySchema((schema as { fields: Parameters<typeof processArraySchema>[0] }).fields);
      }
      return processObjectSchema(schema as Record<string, CredentialSchemaField>);
    }

    if (!provider?.config_schema) return [];

    const schema = provider.config_schema as unknown;
    if (
      typeof schema === 'object' &&
      schema !== null &&
      'fields' in schema &&
      Array.isArray((schema as { fields: unknown[] }).fields)
    ) {
      return processArraySchema((schema as { fields: Parameters<typeof processArraySchema>[0] }).fields);
    }
    return processObjectSchema(schema as Record<string, CredentialSchemaField>);
  };

  const credentialFields = requiresInstance ? [] : getCredentialFields();

  // ============= SALVAR =============

  const handleSave = async () => {
    if (!selectedChannel || !selectedProvider || !name || !identifier) return;

    // Todos os providers exceto webchat exigem instância vinculada
    if (requiresInstance && !selectedInstanceId && availableInstances.length > 0) {
      toast.error('Selecione uma instância disponível');
      return;
    }

    setSaving(true);
    try {
      // -----------------------------------------------------------------------
      // PASSO 0: provisiona automaticamente a instância quando há cota livre.
      // Falha aqui aborta a criação da inbox (nenhum registro é criado).
      // -----------------------------------------------------------------------
      let provisionedInstance: ProvisionedInstance | null = null;
      if (requiresInstance && !selectedInstanceId && canAutoProvision) {
        try {
          provisionedInstance = await autoProvision.mutateAsync(selectedProvider);
          logger.info('[CreateInboxDialog]', 'Instância criada automaticamente', {
            instanceId: provisionedInstance.id,
          });
        } catch (provisionError) {
          const message =
            provisionError instanceof Error
              ? provisionError.message
              : 'Não foi possível criar a instância automaticamente';
          logger.error('[CreateInboxDialog]', 'Falha ao provisionar instância', message);
          toast.error(message);
          setSaving(false);
          return;
        }
      }


      const initialSettings = signatureEnabled
        ? { signature_enabled: true, signature_format: '_*{{agent_name}}:*_' }
        : {};

      // Webchat já nasce ativo; demais aguardam vinculação de instância
      const provider = selectedProviderData;
      const isWebchatProvider = provider?.slug === 'webchat';
      const initialStatus = isWebchatProvider ? 'active' : 'pending';

      const { data: inbox, error: inboxError } = await supabase
        .from('inboxes')
        .insert({
          tenant_id: tenantId,
          provider_id: selectedProvider,
          // Versão resolvida automaticamente (sem etapa de escolha na UI)
          provider_version_id: resolvedVersion?.id || null,
          name,
          channel_type: selectedChannel,
          identifier,
          status: initialStatus,
          settings: initialSettings,
        })
        .select()
        .single();

      if (inboxError) {
        if (inboxError.message.includes('duplicate key')) {
          toast.error('Já existe uma inbox com esse identificador');
        } else if (inboxError.message.includes('row-level security')) {
          toast.error('Limite de caixas de entrada atingido para seu plano');
        } else {
          throw inboxError;
        }
        return;
      }

      let finalCredentials = { ...credentials };

      // Copia credenciais da instância (selecionada manualmente ou criada automaticamente)
      const effectiveInstanceId = selectedInstanceId || provisionedInstance?.id || null;
      if (effectiveInstanceId && requiresInstance) {
        const selectedInstance =
          availableInstances.find((i) => i.id === effectiveInstanceId) || provisionedInstance;
        if (selectedInstance) {
          const instanceCredentials = selectedInstance.credentials;
          if (instanceCredentials && typeof instanceCredentials === 'object') {
            finalCredentials = {
              ...finalCredentials,
              ...(instanceCredentials as Record<string, string>),
            };
          }

          // Retrocompatibilidade com providers legados
          finalCredentials.token = selectedInstance.token || finalCredentials.token || '';
          finalCredentials.instance_id =
            selectedInstance.instance_id || finalCredentials.instance_id || '';

          try {
            await useInstance.mutateAsync({ instanceId: effectiveInstanceId, inboxId: inbox.id });
            logger.info('[CreateInboxDialog]', 'Instância vinculada à inbox', {
              instanceId: effectiveInstanceId,
              inboxId: inbox.id,
            });
          } catch (instanceError) {
            logger.error('[CreateInboxDialog]', 'Erro ao vincular instância', String(instanceError));
            // Continua mesmo se falhar - inbox já foi criada
          }
        }
      }


      if (provider?.slug === 'stress-test') {
        finalCredentials.instance_id = identifier;
      }

      if (Object.keys(finalCredentials).length > 0) {
        const { error: credError } = await supabase
          .from('inbox_credentials')
          .insert({
            inbox_id: inbox.id,
            credentials: finalCredentials,
          });

        if (credError) {
          logger.error('[CreateInboxDialog]', 'Erro ao salvar credenciais', credError.message);
        }
      }

      toast.success('Caixa de entrada criada com sucesso!');
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      logger.error('[CreateInboxDialog]', 'Erro ao criar inbox', error);
      toast.error('Erro ao criar inbox');
    } finally {
      setSaving(false);
    }
  };

  /** Callback de sucesso do Meta Embedded Signup */
  const handleMetaSignupSuccess = () => {
    logger.info('[CreateInboxDialog]', 'Meta Embedded Signup concluído com sucesso', {
      inboxId: pendingMetaInbox?.id,
    });
    setMetaSignupDialogOpen(false);
    setPendingMetaInbox(null);
    toast.success('WhatsApp Cloud API conectado com sucesso!');
    onSuccess();
    onOpenChange(false);
  };

  /** Fechamento do dialog de Meta Signup (cancelado ou concluído) */
  const handleMetaSignupOpenChange = (isOpen: boolean) => {
    setMetaSignupDialogOpen(isOpen);

    if (!isOpen && pendingMetaInbox) {
      logger.info('[CreateInboxDialog]', 'Meta Signup cancelado, inbox permanece em pending_setup', {
        inboxId: pendingMetaInbox.id,
      });
      setPendingMetaInbox(null);
      toast.info('Inbox criada. Complete a conexão nas configurações.');
      onSuccess();
      onOpenChange(false);
    }
  };

  // ============= VALIDAÇÃO =============

  const canSubmit = () => {
    if (!selectedChannel || !selectedProvider) return false;
    if (!name || !identifier || instanceIdConflict || checkingInstanceId) return false;

    // meta-instagram exige apenas o token de verificação nesta etapa
    if (isMetaInstagram) {
      if (!requiresInstance) return !!credentials['webhook_verify_token'];
      return !!selectedInstanceId || canAutoProvision;
    }

    if (requiresInstance) {
      // Cota livre equivale a ter instância: ela será criada automaticamente
      if (canAutoProvision) return true;
      if (availableInstances.length === 0) return false;
      if (!selectedInstanceId) return false;
    }


    return true;
  };

  // ============= HELPERS DE UI =============

  const getIdentifierPlaceholder = () => {
    switch (selectedChannel) {
      case 'whatsapp':
      case 'telegram':
        return '+55 11 99999-9999';
      case 'instagram':
        return '@seu_usuario';
      case 'messenger':
        return 'ID da página';
      case 'webchat':
        return 'chat.seusite.com';
      default:
        return 'Identificador';
    }
  };

  const getIdentifierLabel = () => {
    switch (selectedChannel) {
      case 'whatsapp':
      case 'telegram':
        return 'Número do telefone';
      case 'instagram':
        return 'Username do Instagram';
      case 'messenger':
        return 'ID da Página do Facebook';
      case 'webchat':
        return 'Domínio do site';
      default:
        return 'Identificador';
    }
  };

  // Bloqueio dos campos quando provider requer instância e não há nenhuma disponível
  // Campos só ficam bloqueados quando não há instância nem cota para criar uma
  const fieldsDisabled = requiresInstance && availableInstances.length === 0 && !canAutoProvision;

  // ============= RENDER =============
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden gap-0 flex flex-col max-h-[92vh]"
        preventCloseOnOutsideInteraction
      >
        {/* Cabeçalho com ilustração */}
        <DialogHeader className="relative flex-shrink-0 overflow-hidden px-6 py-6 border-b text-left space-y-0">
          {/* Camada de gradiente + blooms coloridos do cabeçalho */}
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-violet-500/10 via-sky-500/5 to-emerald-500/10"
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -left-10 -top-12 h-40 w-40 rounded-full bg-violet-500/20 blur-3xl"
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-24 -bottom-16 h-40 w-40 rounded-full bg-emerald-500/20 blur-3xl"
          />

          <div className="relative flex items-start gap-4 pr-8">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-500 shadow-lg shadow-violet-500/25">
              <InboxIcon className="h-6 w-6 text-white" strokeWidth={2.2} />
            </div>
            <div className="space-y-1">
              <DialogTitle className="text-2xl font-semibold tracking-tight">
                Nova inbox
              </DialogTitle>
              <DialogDescription className="text-base">
                Conecte um novo canal para começar a atender seus clientes.
              </DialogDescription>
            </div>
          </div>

          {/* Ilustração decorativa (oculta no mobile) */}
          <img
            src={inboxIllustration}
            alt=""
            aria-hidden="true"
            loading="lazy"
            width={1024}
            height={640}
            className="pointer-events-none absolute right-10 -top-3 hidden h-[150px] w-auto select-none drop-shadow-xl md:block"
          />
        </DialogHeader>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-6 space-y-6">
          {/* Seleção de canal */}
          <section className="space-y-3">
            <Label className="text-sm font-semibold">Escolha o canal que deseja conectar</Label>
            <ChannelPicker
              value={selectedChannel}
              onChange={(channel) => setSelectedChannel(channel)}
              disabled={saving}
            />
          </section>


          {/* Formulário — só aparece após escolher o canal */}
          {selectedChannel && (
            <>
              {loadingProviders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : providers.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  Nenhum provedor disponível para este canal.
                </div>
              ) : (
                <>
                  {/* Alertas contextuais */}
                  {isMetaInstagram && (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertTitle>Configuração em 2 etapas</AlertTitle>
                      <AlertDescription>
                        Crie a inbox agora e finalize a configuração do webhook no Meta Developer Portal.
                      </AlertDescription>
                    </Alert>
                  )}

                  {isMetaInstagram && !requiresInstance && (
                    <InstagramSetupGuide
                      webhookUrl={getInstagramWebhookUrl()}
                      verifyToken={credentials['webhook_verify_token'] || ''}
                      defaultExpanded={false}
                    />
                  )}

                  {/* Instância pendente de liberação */}
                  {requiresInstance && availableInstances.length === 0 && hasPendingInstances && (
                    <InboxInstanceNotice
                      variant="pending"
                      channelType={selectedChannel}
                      providerName={getProviderDisplayName()}
                    />
                  )}

                  {/* Cota livre: instância será criada automaticamente ao salvar */}
                  {canAutoProvision && (
                    <InboxInstanceNotice
                      variant="auto"
                      channelType={selectedChannel}
                      providerName={getProviderDisplayName()}
                    />
                  )}

                  {/* Sem instância disponível: aviso comercial antes da configuração */}
                  {showInstanceRequired && (
                    <div ref={instanceNoticeRef} tabIndex={-1} className="scroll-mt-4">
                      <InboxInstanceNotice
                        variant="required"
                        channelType={selectedChannel}
                        providerName={getProviderDisplayName()}
                        onViewPlans={() => {
                          onOpenChange(false);
                          navigate('/tenant/plans');
                        }}
                      />
                    </div>
                  )}


                  {/* Cartão de configuração com acento em gradiente */}
                  <div className="relative overflow-hidden rounded-2xl border bg-card/60 p-5 space-y-5">
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-violet-500 via-sky-500 to-emerald-500"
                  />
                  {/* Título da seção de configuração */}
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 shadow-sm">
                      <SlidersHorizontal className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="text-base font-semibold text-foreground">
                      Configure sua inbox
                    </h3>
                  </div>

                  {/* Grid principal em 2 colunas */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                    {/* Coluna 1: identidade da inbox */}
                    <div className="space-y-5">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-sm font-semibold">Nome da inbox</Label>
                        <Input
                          id="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Ex: WhatsApp Vendas"
                          className="h-11 rounded-xl"
                          disabled={fieldsDisabled}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="identifier" className="text-sm font-semibold">{getIdentifierLabel()}</Label>
                        {selectedChannel === 'whatsapp' ||
                        selectedChannel === 'whatsapp_oficial' ||
                        selectedChannel === 'telegram' ? (
                          <PhoneInput
                            id="identifier"
                            value={identifier}
                            onChange={(value) => setIdentifier(value)}
                            placeholder="(11) 99999-9999"
                            className="[&_input]:h-11 [&_input]:rounded-xl [&_button]:h-11 [&_button]:rounded-xl"
                            disabled={fieldsDisabled}
                          />
                        ) : (
                          <Input
                            id="identifier"
                            value={identifier}
                            onChange={(e) => setIdentifier(e.target.value)}
                            placeholder={getIdentifierPlaceholder()}
                            className="h-11 rounded-xl"
                            disabled={fieldsDisabled}
                          />
                        )}
                      </div>

                      {/* Provedor: read-only quando único, select quando múltiplos */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <Label htmlFor="provider-select" className="text-sm font-semibold">Provedor</Label>
                          {providers.length === 1 && (
                            <Badge variant="secondary" className="text-[10px] uppercase tracking-tight">
                              Auto-selecionado
                            </Badge>
                          )}
                        </div>

                        {providers.length === 1 ? (
                          <div className="flex items-center h-11 px-3 rounded-xl border bg-muted/50 text-sm text-muted-foreground">
                            {providers[0].display_name || providers[0].name}
                          </div>
                        ) : (
                          <Select
                            value={selectedProvider || ''}
                            onValueChange={(value) => {
                              setSelectedProvider(value);
                              setCredentials({});
                              setSelectedInstanceId(null);
                            }}
                          >
                            <SelectTrigger id="provider-select" className="h-11 rounded-xl">
                              <SelectValue placeholder="Selecione o provedor..." />
                            </SelectTrigger>
                            <SelectContent>
                              {providers.map((provider) => (
                                <SelectItem key={provider.id} value={provider.id}>
                                  {provider.display_name || provider.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    </div>

                    {/* Coluna 2: instância e credenciais */}
                    <div className="space-y-5">
                      {requiresInstance && availableInstances.length > 0 && (
                        <div className="space-y-2">
                          <Label htmlFor="instance-select" className="text-sm font-semibold">Conexão disponível</Label>
                          <Select
                            value={selectedInstanceId || ''}
                            onValueChange={(value) => setSelectedInstanceId(value)}
                          >
                            <SelectTrigger id="instance-select" className="h-11 rounded-xl">
                              <SelectValue placeholder="Selecione uma conexão..." />
                            </SelectTrigger>
                            <SelectContent>
                              {availableInstances.map((instance) => (
                                <SelectItem key={instance.id} value={instance.id}>
                                  {instance.instance_name || instance.instance_id || 'Instância sem nome'}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-muted-foreground">
                            Conexões {getProviderDisplayName()} liberadas pelo administrador.
                          </p>
                        </div>
                      )}

                      <InboxCredentialsFields
                        fields={credentialFields}
                        values={credentials}
                        onChange={setCredentials}
                        instanceIdConflict={instanceIdConflict}
                        checkingInstanceId={checkingInstanceId}
                        disabled={fieldsDisabled}
                      />

                      {/* Assinatura do agente (com preview do balão) */}
                      {!fieldsDisabled && (
                        <AgentSignaturePreviewCard
                          enabled={signatureEnabled}
                          onChange={setSignatureEnabled}
                          agentName={profile?.full_name}
                        />
                      )}
                    </div>
                  </div>
                  </div>




                </>
              )}
            </>
          )}

        </div>


        {/* Rodapé */}
        <div className="flex-shrink-0 px-6 py-4 border-t bg-muted/30 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button
            size="lg"
            className="rounded-xl bg-gradient-to-r from-violet-600 to-indigo-500 text-white shadow-lg shadow-violet-500/25 hover:from-violet-600/90 hover:to-indigo-500/90"
            onClick={handleSave}
            disabled={!canSubmit() || saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Criando...
              </>
            ) : (
              <>
                Criar inbox
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>

      </DialogContent>

      {/* Modal de limite atingido */}
      <LimitReachedModal
        open={limitReachedModalOpen}
        onOpenChange={setLimitReachedModalOpen}
        resourceType="inbox"
        currentCount={limits?.effective_max_inboxes}
        maxAllowed={limits?.effective_max_inboxes}
      />

      {/* Dialog de Meta Embedded Signup */}
      {pendingMetaInbox && (
        <MetaEmbeddedSignupDialog
          open={metaSignupDialogOpen}
          onOpenChange={handleMetaSignupOpenChange}
          inboxId={pendingMetaInbox.id}
          inboxName={pendingMetaInbox.name}
          onSuccess={handleMetaSignupSuccess}
        />
      )}
    </Dialog>
  );
}
